import type { ReactElement, ReactNode } from 'react';
import { useGamesSite } from '../context';
import { GameRow } from '../components/GameRow';
import { GameShelf, TopChart } from '../components/GameShelf';
import { GenreGrid } from '../components/GenreGrid';
import { HeroCarousel } from '../components/HeroCarousel';
import { SearchForm } from '../components/SearchForm';
import { gameCount } from '../format';
import { gamesGenrePath } from '../routes';
import type { GameCard, GameGenreSummary, GamesHomeData } from '../types';

/** The scope every Games page renders inside: its palette, type and light/dark. */
export function GamesSurface({ children }: { children: ReactNode }): ReactElement {
  return <div className="fias-games">{children}</div>;
}

/** The page's big title with the search field beside it. */
function PageHead({ search }: { search?: ReactNode }): ReactElement {
  return (
    <header className="fg-page-head">
      <h1 className="fg-large-title">Games</h1>
      {search ?? <SearchForm />}
    </header>
  );
}

/**
 * The Games home: featured games, what's new, the top games, and every genre.
 * `search` replaces the plain search form (the app's live search); `results`
 * replaces the shelves while a search is showing.
 */
export function GamesHomeBody({
  data,
  search,
  results,
}: {
  data: GamesHomeData;
  search?: ReactNode;
  results?: ReactNode;
}): ReactElement {
  const empty =
    data.featured.length === 0 &&
    data.newest.length === 0 &&
    data.top.length === 0 &&
    data.genres.length === 0;
  return (
    <div className="fg-wrap">
      <PageHead search={search} />
      {results ??
        (empty ? (
          <div className="fg-empty">
            <p className="fg-empty__title">No games yet.</p>
            <p>Games show up here as soon as their makers publish them.</p>
          </div>
        ) : (
          <>
            <HeroCarousel featured={data.featured} />
            <GameShelf id="new" title="New and updated" games={data.newest} />
            <TopChart id="top" title="Top games" games={data.top} />
            <GenreGrid genres={data.genres} />
            {data.genres.map((genre) => (
              <GameShelf
                key={genre.id}
                id={`genre-${genre.id}`}
                title={`${genre.label} games`}
                games={genre.games}
                layout="tiles"
                seeAllHref={gamesGenrePath(genre.id)}
                seeAllLabel={`See all ${genre.label.toLowerCase()} games`}
              />
            ))}
          </>
        ))}
    </div>
  );
}

/**
 * Search results, in place of the home's shelves. `status` is announced to
 * screen readers as results change.
 */
export function SearchResultsBody({
  query,
  games,
  pending = false,
  genres = [],
}: {
  query: string;
  games: GameCard[];
  pending?: boolean;
  /** Offered when nothing matches. */
  genres?: GameGenreSummary[];
}): ReactElement {
  const { Link } = useGamesSite();
  const status = pending
    ? 'Searching…'
    : games.length === 0
      ? `No games match “${query}”.`
      : `${gameCount(games.length)} for “${query}”`;
  return (
    <section className="fg-results" aria-labelledby="fg-results-title">
      <h2 id="fg-results-title" className="fg-h2" role="status" aria-live="polite">
        {status}
      </h2>
      {!pending && games.length === 0 && genres.length > 0 && (
        <div className="fg-results__empty">
          <p>Try a genre instead:</p>
          <ul className="fg-chips">
            {genres
              .filter((g) => g.count > 0)
              .map((g) => (
                <li key={g.id}>
                  <Link href={gamesGenrePath(g.id)} className="fg-chip">
                    {g.label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      )}
      {games.length > 0 && (
        <ul className="fg-list" aria-busy={pending || undefined}>
          {games.map((game) => (
            <li key={game.slug}>
              <GameRow game={game} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
